import { Layers, Upload, X, Trash2, ArrowRight } from "lucide-react";
import { formatByType } from "../../lib/format";

function metricsOf(scn) {
  return [{ ...scn.primary, _primary: true }, ...(scn.secondary || [])];
}

function delta(a, b, type) {
  if (type === "raw" || typeof a !== "number" || typeof b !== "number") return null;
  const d = b - a;
  if (Math.abs(d) < 0.005) return { text: "same", cls: "text-muted-foreground" };
  const sign = d > 0 ? "+" : "−";
  const cls = d > 0 ? "text-brand" : "text-action";
  return { text: `${sign}${formatByType(Math.abs(d), type)}`, cls };
}

export default function ScenarioCompare({ scenarios, onLoad, onRemove, onClear }) {
  if (!scenarios.length) return null;
  // Use the union of metric labels from the first scenario (they share the same calculator).
  const base = scenarios[0];
  const rows = metricsOf(base).map((m) => m.label);

  return (
    <div data-testid="scenario-compare" className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground">
          <Layers className="h-5 w-5 text-brand" /> Compare scenarios
        </h3>
        <button onClick={onClear} data-testid="scenario-clear" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" /> Clear all
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="py-2 pr-4 text-left font-medium text-muted-foreground">Metric</th>
              {scenarios.map((s, i) => (
                <th key={s.id} className="px-3 py-2 text-right">
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-heading font-bold text-foreground">{s.name}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => onLoad(s.values)} data-testid={`scenario-load-${i}`} title="Load into calculator"
                        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-brand"><Upload className="h-3.5 w-3.5" /></button>
                      <button onClick={() => onRemove(s.id)} data-testid={`scenario-remove-${i}`} title="Remove"
                        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((label, ri) => {
              const cells = scenarios.map((s) => metricsOf(s).find((m) => m.label === label));
              const first = cells[0];
              const isPrimary = first?._primary;
              return (
                <tr key={ri} className={`border-b border-border/60 ${isPrimary ? "bg-brand/5" : ""}`}>
                  <td className={`py-2.5 pr-4 ${isPrimary ? "font-heading font-bold text-brand" : "text-foreground/80"}`}>{label}</td>
                  {cells.map((m, ci) => {
                    const d = ci > 0 && first ? delta(
                      first.type === "raw" ? first.value : Number(first.value),
                      m && m.type === "raw" ? m.value : Number(m?.value),
                      first.type
                    ) : null;
                    return (
                      <td key={ci} className="px-3 py-2.5 text-right">
                        <div className={`font-semibold ${isPrimary ? "text-brand" : "text-foreground"}`}>
                          {m ? (m.type === "raw" ? m.value : formatByType(m.value, m.type)) : "—"}
                        </div>
                        {d && ci > 0 && <div className={`text-xs ${d.cls}`}>{d.text} vs {scenarios[0].name}</div>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {scenarios.length === 1 && (
        <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <ArrowRight className="h-3.5 w-3.5" /> Change your inputs and save another scenario to compare side by side.
        </p>
      )}
    </div>
  );
}
